import {
  addCalendarDate,
  computeOverdueForLease,
  doesLeaseRespectBalance,
  doesLeaseRespectLatePayment,
  endOfCalendarMonth,
  endOfCalendarYear,
  getNumberOfMonthsBetweenCalendarDates,
  Invoice,
  isEntityInClassicFilters,
  isLeaseIncludedInManagementFilters,
  isNilOrEmpty,
  isWithinInterval,
  LeaseExtended,
  leaseFinishedInPeriod,
  startOfCalendarMonth,
  startOfCalendarYear,
  subtractCalendarDate,
  toCalendarDate,
  today,
  type CalendarDate,
  type Filter,
  type FilterGroup,
} from '@rentguru/commons-utils';
import { isEmpty, isNil } from 'lodash';

import { PaymentProviderMandateStatus } from 'src/API';
import {
  LeaseDatePreset,
  LeaseDurationOption,
  LeasesActiveFilters,
  LeasesArchivedFilters,
  LeasesCommonFilters,
  LeasesDraftsFilters,
  type LeaseDateFilter,
} from 'src/hooks/FiltersContext';

const MONTHS_IN_ONE_YEAR = 12;
const MONTHS_IN_THREE_YEARS = 36;
const MONTHS_IN_NINE_YEARS = 108;

const CLASSIC_FILTER_NAMES = ['building', 'unit', 'type', 'tenants', 'city', 'owner'];
const CONTAINED_CLASSIC_FILTER_NAMES = ['unit', 'building', 'tenants'];

export const getClassicFilterItems = (filters: Filter[], name: string): string[] =>
  filters.find((filter) => filter.name === name)?.items ?? [];

export const toggleClassicFilterItem = (filters: Filter[], name: string, field: string, itemId: string): Filter[] => {
  const currentItems = getClassicFilterItems(filters, name);
  const items = currentItems.includes(itemId)
    ? currentItems.filter((currentItem) => currentItem !== itemId)
    : [...currentItems, itemId];
  const otherFilters = filters.filter((filter) => filter.name !== name);
  if (isEmpty(items)) {
    return otherFilters;
  }
  return [...otherFilters, { name, field, items }];
};

export const getLeaseDateFilterInterval = (
  dateFilter: LeaseDateFilter
): { from: CalendarDate; to: CalendarDate } | null => {
  const { preset, from, to } = dateFilter;
  switch (preset) {
    case LeaseDatePreset.THIS_MONTH:
      return { from: startOfCalendarMonth(today()), to: endOfCalendarMonth(today()) };
    case LeaseDatePreset.NEXT_MONTH: {
      const nextMonth = addCalendarDate(startOfCalendarMonth(today()), 1, 'months');
      return { from: nextMonth, to: endOfCalendarMonth(nextMonth) };
    }
    case LeaseDatePreset.THIS_YEAR:
      return { from: startOfCalendarYear(today()), to: endOfCalendarYear(today()) };
    case LeaseDatePreset.LAST_YEAR: {
      const lastYear = subtractCalendarDate(today(), 1, 'years');
      return { from: startOfCalendarYear(lastYear), to: endOfCalendarYear(lastYear) };
    }
    default:
      if (isNil(from) && isNil(to)) {
        return null;
      }
      return { from: from ?? toCalendarDate('1900-01-01'), to: to ?? toCalendarDate('2999-12-31') };
  }
};

export const isLeaseDateFilterActive = (dateFilter: LeaseDateFilter): boolean =>
  !isNil(getLeaseDateFilterInterval(dateFilter));

const doesLeaseMatchStartDateFilter = (lease: LeaseExtended, dateFilter: LeaseDateFilter): boolean => {
  const interval = getLeaseDateFilterInterval(dateFilter);
  if (isNil(interval)) {
    return true;
  }
  return isWithinInterval(toCalendarDate(lease.startDate), { start: interval.from, end: interval.to });
};

export const getLeaseDurationOption = (lease: LeaseExtended): LeaseDurationOption => {
  const months = getNumberOfMonthsBetweenCalendarDates(
    toCalendarDate(lease.startDate),
    toCalendarDate(lease.endDate),
    true
  );
  if (months < MONTHS_IN_ONE_YEAR) {
    return LeaseDurationOption.UNDER_ONE_YEAR;
  }
  if (months < MONTHS_IN_THREE_YEARS) {
    return LeaseDurationOption.ONE_TO_THREE_YEARS;
  }
  if (months < MONTHS_IN_NINE_YEARS) {
    return LeaseDurationOption.THREE_TO_NINE_YEARS;
  }
  return LeaseDurationOption.NINE_YEARS_AND_MORE;
};

const doesLeaseMatchDurations = (lease: LeaseExtended, durations: LeaseDurationOption[]): boolean => {
  if (isEmpty(durations)) {
    return true;
  }
  return durations.includes(getLeaseDurationOption(lease));
};

const doesLeaseMatchSubletting = (lease: LeaseExtended, subletting: boolean | null): boolean => {
  if (isNil(subletting)) {
    return true;
  }
  return (lease.subletting ?? false) === subletting;
};

const isCommonFilterSelected = (leaseFilters: LeasesCommonFilters): boolean => {
  const { filters, startDateFilter, durations, subletting } = leaseFilters;
  return (
    CLASSIC_FILTER_NAMES.some((name) => !isEmpty(getClassicFilterItems(filters, name))) ||
    isLeaseDateFilterActive(startDateFilter) ||
    !isEmpty(durations) ||
    !isNil(subletting)
  );
};

const doesLeaseMatchCommonFilters = (lease: LeaseExtended, leaseFilters: LeasesCommonFilters): boolean => {
  const { filters, startDateFilter, durations, subletting } = leaseFilters;
  if (!isEmpty(filters) && !isLeaseInClassicFilters(lease, filters)) {
    return false;
  }
  return (
    doesLeaseMatchStartDateFilter(lease, startDateFilter) &&
    doesLeaseMatchDurations(lease, durations) &&
    doesLeaseMatchSubletting(lease, subletting)
  );
};

export const isLeaseOverdue = (lease: LeaseExtended, unpaidInvoices: Invoice[]): boolean =>
  !isNil(computeOverdueForLease(lease, unpaidInvoices, today()));

export interface LeasesActiveFilterData {
  unpaidInvoicesOfClient: Invoice[] | null;
  unpaidRentInvoices: Invoice[] | null;
  leaseIdsWithPriceChangeToValidate: Set<string> | null;
}

const isExcludedByOverdueFilter = (
  lease: LeaseExtended,
  overdueOnly: boolean,
  unpaidRentInvoices: Invoice[] | null
): boolean => overdueOnly && (isNil(unpaidRentInvoices) || !isLeaseOverdue(lease, unpaidRentInvoices));

const isExcludedByPriceChangeFilter = (
  lease: LeaseExtended,
  priceChangeToValidate: boolean,
  leaseIdsWithPriceChangeToValidate: Set<string> | null
): boolean =>
  priceChangeToValidate &&
  (isNil(leaseIdsWithPriceChangeToValidate) || !leaseIdsWithPriceChangeToValidate.has(lease.id));

export const isOneLeaseActiveFilterSelected = (leaseFilters: LeasesActiveFilters) => {
  const { balanceDept, balanceLatePaymentMonth, overdueOnly, priceChangeToValidate, endingInFromTo, mandateStatus } =
    leaseFilters;
  return (
    balanceDept !== 0 ||
    balanceLatePaymentMonth !== 0 ||
    overdueOnly ||
    priceChangeToValidate ||
    !isNil(endingInFromTo) ||
    !isEmpty(mandateStatus) ||
    isCommonFilterSelected(leaseFilters)
  );
};

export const filterLeasesActiveByFilters = <T extends LeaseExtended>(
  leases: T[],
  leaseFilters: LeasesActiveFilters,
  { unpaidInvoicesOfClient, unpaidRentInvoices, leaseIdsWithPriceChangeToValidate }: LeasesActiveFilterData,
  managementFilters?: FilterGroup[]
) => {
  const hasManagerFilters = !isNilOrEmpty(managementFilters);
  if (!isOneLeaseActiveFilterSelected(leaseFilters) && !hasManagerFilters) {
    return leases;
  }
  const { balanceDept, balanceLatePaymentMonth, overdueOnly, priceChangeToValidate, endingInFromTo, mandateStatus } =
    leaseFilters;
  return leases.filter((lease) => {
    if (!doesLeaseMatchCommonFilters(lease, leaseFilters)) {
      return false;
    }
    if (balanceDept !== 0 && !doesLeaseRespectBalance(lease, balanceDept)) {
      return false;
    }
    if (isExcludedByOverdueFilter(lease, overdueOnly, unpaidRentInvoices)) {
      return false;
    }
    if (isExcludedByPriceChangeFilter(lease, priceChangeToValidate, leaseIdsWithPriceChangeToValidate)) {
      return false;
    }
    if (
      balanceLatePaymentMonth !== 0 &&
      unpaidInvoicesOfClient !== null &&
      !doesLeaseRespectLatePayment(lease, balanceLatePaymentMonth, unpaidInvoicesOfClient)
    ) {
      return false;
    }
    if (endingInFromTo && !leaseFinishedInPeriod(lease, endingInFromTo.from ?? today(), endingInFromTo.to ?? today())) {
      return false;
    }
    if (!isEmpty(mandateStatus)) {
      const leaseMandateStatus = lease.paymentOption?.mandateStatus as PaymentProviderMandateStatus | null | undefined;
      if (isNil(leaseMandateStatus) || !(mandateStatus ?? []).includes(leaseMandateStatus)) {
        return false;
      }
    }
    if (hasManagerFilters) {
      return isLeaseIncludedInManagementFilters(lease, managementFilters);
    }

    return true;
  });
};

export const isOneLeaseDraftsFilterSelected = (leaseFilters: LeasesDraftsFilters) =>
  isCommonFilterSelected(leaseFilters) || !isEmpty(getClassicFilterItems(leaseFilters.filters, 'status'));

export const filterLeasesDraftsByFilters = <T extends LeaseExtended>(
  leases: T[],
  leaseFilters: LeasesDraftsFilters,
  managementFilters?: FilterGroup[]
) => {
  const hasManagerFilters = !isNilOrEmpty(managementFilters);
  if (!isOneLeaseDraftsFilterSelected(leaseFilters) && !hasManagerFilters) {
    return leases;
  }

  return leases.filter((lease) => {
    if (!doesLeaseMatchCommonFilters(lease, leaseFilters)) {
      return false;
    }
    if (hasManagerFilters) {
      return isLeaseIncludedInManagementFilters(lease, managementFilters);
    }
    return true;
  });
};

export const isOneLeaseArchivedFilterSelected = (leaseFilters: LeasesArchivedFilters) => {
  const { balanceDept, overdueOnly, endedSinceFromTo } = leaseFilters;
  return balanceDept !== 0 || overdueOnly || !isNil(endedSinceFromTo) || isCommonFilterSelected(leaseFilters);
};

export const filterLeasesArchivedByFilters = <T extends LeaseExtended>(
  leases: T[],
  leaseFilters: LeasesArchivedFilters,
  unpaidRentInvoices: Invoice[] | null,
  managementFilters?: FilterGroup[]
) => {
  const hasManagerFilters = !isNilOrEmpty(managementFilters);
  if (!isOneLeaseArchivedFilterSelected(leaseFilters) && !hasManagerFilters) {
    return leases;
  }
  const { balanceDept, overdueOnly, endedSinceFromTo } = leaseFilters;
  return leases.filter((lease) => {
    if (!doesLeaseMatchCommonFilters(lease, leaseFilters)) {
      return false;
    }
    if (balanceDept !== 0 && !doesLeaseRespectBalance(lease, balanceDept)) {
      return false;
    }
    if (isExcludedByOverdueFilter(lease, overdueOnly, unpaidRentInvoices)) {
      return false;
    }
    if (
      endedSinceFromTo &&
      !leaseFinishedInPeriod(lease, endedSinceFromTo.from ?? today(), endedSinceFromTo.to ?? today())
    ) {
      return false;
    }
    if (hasManagerFilters) {
      return isLeaseIncludedInManagementFilters(lease, managementFilters);
    }
    return true;
  });
};

export const isLeaseInClassicFilters = (lease: LeaseExtended, filters: Filter[]) => {
  const containedFilter = filters.filter((filter) => CONTAINED_CLASSIC_FILTER_NAMES.includes(filter.name));
  const isInContainedFilter = containedFilter.reduce((result, currentFilter) => {
    if (isEmpty(currentFilter.items)) {
      return result;
    }
    if (currentFilter.name === 'unit') {
      const isUnitInThisLease = lease.units?.some(
        (unitLease) => unitLease.unit && currentFilter.items.includes(unitLease.unit.id)
      );
      if (!isUnitInThisLease) {
        return false;
      }
    }
    if (currentFilter.name === 'building') {
      const isBuildingInThisLease = lease.units?.some(
        (unitLease) =>
          unitLease.unit && unitLease.unit.building && currentFilter.items.includes(unitLease.unit.building.id)
      );
      if (!isBuildingInThisLease) {
        return false;
      }
    }
    if (currentFilter.name === 'tenants') {
      const isTenantInTheLease = lease.tenants?.some(
        (tenantLease) => tenantLease && currentFilter.items.includes(tenantLease.id)
      );
      if (!isTenantInTheLease) {
        return false;
      }
    }

    return result;
  }, true);
  const otherFilters = filters.filter((filter) => !CONTAINED_CLASSIC_FILTER_NAMES.includes(filter.name));
  const isInClassicFilter = isEntityInClassicFilters(lease, otherFilters);
  return isInContainedFilter && isInClassicFilter;
};
